/**
 * @fileoverview `AnnouncementRegion` atom — visually hidden ARIA live
 * region for screen-reader announcements (WEB-132).
 *
 * Purpose: announce dynamic UI changes (filter selection, transient
 * status updates, errors) to assistive technologies without displacing
 * layout. Mount once near the top of a page or surface, then drive its
 * `message` prop from URL state, server data, or interaction handlers.
 *
 * Design choices:
 *   - `role="status"` for `polite` (the implicit role) makes the
 *     contract explicit and survives axe rule changes.
 *   - `role="alert"` for `assertive` matches the accepted pattern
 *     for time-sensitive updates (errors, validation failures).
 *   - `aria-atomic="true"` ensures screen readers announce the entire
 *     message on each update instead of diffing partial fragments.
 *   - `sr-only` keeps the region visually hidden while remaining in
 *     the accessibility tree.
 *
 * @since WEB-132
 */
import * as React from 'react';
import { cn } from '@/components/atoms/primitives/utils';

export interface AnnouncementRegionProps {
  /**
   * Text the screen reader announces. When this changes, the live
   * region re-announces the new value. An empty string is a valid
   * "no current announcement" state.
   */
  message: string;
  /**
   * `polite` (default) waits for the user to be idle before reading.
   * `assertive` interrupts the current speech queue — reserve for
   * time-sensitive updates (errors, lost-connection toasts).
   */
  politeness?: 'polite' | 'assertive';
  /** Optional override for the wrapper class (e.g. positioning). */
  className?: string;
}

/**
 * Visually hidden ARIA live region. See file-level docstring.
 */
export function AnnouncementRegion({
  message,
  politeness = 'polite',
  className,
}: AnnouncementRegionProps): React.ReactElement {
  const role = politeness === 'assertive' ? 'alert' : 'status';

  return (
    <div
      role={role}
      aria-live={politeness}
      aria-atomic="true"
      className={cn('sr-only', className)}
      data-testid="announcement-region"
    >
      {message}
    </div>
  );
}
